/**
 * Typed wrapper over the API.
 *
 * Everything goes through `/api`, which Vite proxies to the Fastify server in
 * development and which sits behind the same origin in production. Same-origin
 * matters: the session is an httpOnly first-party cookie, and third-party
 * cookies are blocked by default in every browser shipping today.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  })

  const text = await res.text()
  const body = text ? (JSON.parse(text) as unknown) : null

  if (!res.ok) {
    const payload = body as { error?: string; code?: string } | null
    throw new ApiError(payload?.error ?? `Request failed (${res.status})`, res.status, payload?.code)
  }
  return body as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
}

/* ------------------------------------------------------------------ *
 * Response shapes
 * ------------------------------------------------------------------ */

export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic'
export type Tier = 'starter' | 'pro' | 'silver' | 'elite' | 'gold' | 'diamond'

export type AvatarKind = 'identicon' | 'preset' | 'token'

/** The private view of your own account. Never returned for anyone else. */
export interface User {
  id: string
  address: string
  /** Identicon seed. Derived from the address, so it can be shown publicly. */
  avatarSeed: string
  username: string | null
  bio: string | null
  avatarKind: AvatarKind
  avatarRef: string | null
  profilePublic: boolean
  balanceCents: number
  /** Bonus credit not yet played through: spendable on packs, not withdrawable. */
  lockedCents: number
  totalStakedCents: number
  totalReturnedCents: number
  realisedRtp: number | null
  createdAt: string
}

/**
 * How somebody else appears on the ticker, the board, or a profile header.
 *
 * `username` is null exactly when there is nothing to link to — a private
 * account — so the presence of the link and the presence of the name are the
 * same check and cannot drift apart.
 */
export interface Identity {
  username: string | null
  handle: string
  avatarKind: AvatarKind
  avatarRef: string | null
  seed: string | null
  private: boolean
}

export interface ProfileStats {
  opens: number
  cards: number
  realisedRtp: number | null
  bestMultiple: number | null
  bestRarity: Rarity | null
  favouriteTier: string | null
  firstOpenAt: string | null
}

export interface ShowcasePull {
  id: string
  openId: string
  rarity: Rarity
  symbol: string
  kind: 'stock' | 'coin' | 'crypto'
  name: string
  quantity: number
  valueCents: number
  multiple: number
  packName: string
  openedAt: string
}

export interface DashboardResponse {
  user: User
  stats: ProfileStats & { stakedCents: number; returnedCents: number }
  bestPulls: ShowcasePull[]
  vault: { count: number; basisCents: number; valueCents: number }
  recentOpens: Array<{
    id: string
    packId: string
    packName: string
    priceCents: number
    totalCents: number
    multiple: number
    best: Rarity
    openedAt: string
  }>
}

export interface PublicProfile {
  identity: Identity
  bio: string | null
  joinedAt: string
  stats: ProfileStats
  bestPulls: ShowcasePull[]
}

export interface AvatarOptions {
  presets: string[]
  tokens: Array<{ symbol: string; kind: string; count: number }>
}

export interface UsernameCheck {
  available: boolean
  reason: 'format' | 'reserved' | 'taken' | null
  help: string
}

export interface PackSummary {
  /** `tier-cards`. Everything about the configuration is in here. */
  id: string
  name: string
  tagline: string
  price: number
  /** Price at the shortest card count — the tier's headline number. */
  basePrice: number
  rtp: number
  tier: Tier
  accent: string
  cards: number
  hits: number
  art: string | null
  /** Price at every card count, so the slider never waits on the network. */
  prices: Record<number, number>
}

export interface PacksResponse {
  packs: PackSummary[]
  cards: { min: number; max: number; default: number }
}

export interface OddsRow {
  rarity: Rarity
  perPack: number
  expectedCount: number
  lo: number
  hi: number
  evShare: number
}

export interface PackStats {
  mean: number
  median: number
  p10: number
  p90: number
  breakEvenRate: number
  moonRate: number
  iterations: number
}

export interface PackDetail {
  pack: PackSummary & {
    slots: Array<{ index: number; label: string; probabilities: Partial<Record<Rarity, number>> }>
  }
  odds: OddsRow[]
  stats: PackStats
  pool: { size: number; minMarketCap: number; refreshedAt: string }
}

export interface ReferralSummary {
  code: string | null
  signupBonusCents: number
  depositSharePercent: number
  depositCapCents: number
  wagerMultiple: number
  lockedCents: number
  referredBy: { username: string | null; at: string } | null
  canRedeem: boolean
  totalEarnedCents: number
  referrals: Array<{
    username: string | null
    joinedAt: string
    deposited: boolean
    earnedCents: number
  }>
}

export interface DepositRow {
  id: string
  txHash: string
  /** Which chain it landed on. The treasury is one address across all of them. */
  chainId: number
  asset: string
  amountCents: number
  status: 'seen' | 'credited' | 'unattributed' | 'ignored'
  /** Confirmations so far, clamped to what's required. Only climbs while `seen`. */
  confirmations: number
  /** What this row's own chain requires, which differs between chains. */
  confirmationsRequired: number
  seenAt: string
  creditedAt: string | null
}

export interface WithdrawalRow {
  id: string
  toAddress: string
  amountCents: number
  status: 'requested' | 'approved' | 'signed' | 'broadcast' | 'confirmed' | 'failed' | 'rejected'
  txHash: string | null
  failure: string | null
  requestedAt: string
  confirmedAt: string | null
  cancellable: boolean
}

/**
 * `enabled: false` is a normal answer, not an error: a deployment that does not
 * move real money is a supported configuration and the page renders it calmly.
 */
export type WalletResponse =
  | { enabled: false }
  | {
      enabled: true
      chain: { id: number; name: string }
      /** What withdrawals pay out in. Never varies with what was deposited. */
      token: { address: string; symbol: string; decimals: number }
      /**
       * Every chain money can come in on, and what it accepts there. The
       * treasury address is the same on all of them, but the confirmation wait
       * is not, so each carries its own.
       */
      depositChains: {
        chainId: number
        chainName: string
        treasury: string
        confirmations: number
        assets: {
          symbol: string
          address: string | null
          decimals: number
          /** `priced` means the credited amount depends on a quote at deposit time. */
          pricing: 'stable' | 'priced'
        }[]
      }[]
      treasury: string
      confirmations: number
      limits: {
        minDepositCents: number
        minWithdrawalCents: number
        maxWithdrawalCents: number
        dailyRemainingCents: number
        autoApproveUnderCents: number
      }
      /** The only destination a withdrawal can have. Shown, never chosen. */
      withdrawTo: string
      availableCents: number
      deposits: DepositRow[]
      withdrawals: WithdrawalRow[]
    }

export interface Asset {
  symbol: string
  kind: 'stock' | 'coin' | 'crypto'
  name: string
  price: number
  marketCap: number | null
  change24h: number
  contract?: string | null
  logoUrl?: string | null
}

export interface MarketResponse {
  refreshedAt: string
  minMarketCap: number
  source: string | null
  count: number
  assets: Asset[]
}

export interface Pull {
  slot: string
  slotIndex: number
  rarity: Rarity
  asset: Asset
  quantity: number
  value: number
  multiple: number
  rolls: number[]
}

export interface OpenResult {
  open: {
    id: string
    packId: string
    packName: string
    price: number
    pulls: Pull[]
    total: number
    multiple: number
    best: Rarity
    fairness: { serverSeedHash: string; clientSeed: string; nonce: number }
    openedAt: number
  }
  balanceCents: number
  positionIds: string[]
}

export interface Position {
  id: string
  openId: string
  symbol: string
  kind: 'stock' | 'coin' | 'crypto'
  name: string
  quantity: number
  rarity: Rarity
  packId: string
  packName: string
  acquiredAt: string
  basisCents: number
  priceNow: number
  valueCents: number
  changeCents: number
}

export interface VaultResponse {
  positions: Position[]
  totals: { count: number; basisCents: number; valueCents: number }
}

export interface FeedPull {
  id: string
  openId: string
  rarity: Rarity
  symbol: string
  kind: 'stock' | 'coin' | 'crypto'
  name: string
  quantity: number
  valueCents: number
  multiple: number
  packId: string
  packName: string
  openedAt: string
  by: Identity
}

export interface LeaderboardEntry {
  rank: number
  openId: string
  by: Identity
  packId: string
  packName: string
  priceCents: number
  totalCents: number
  multiple: number
  best: Rarity
  openedAt: string
  topPull: { symbol: string; name: string; rarity: Rarity; valueCents: number } | null
}

export interface SeedPairView {
  id: string
  serverSeedHash: string
  clientSeed: string
  nonce: number
  createdAt: string
  retiredAt: string | null
  serverSeed: string | null
}

export interface FairnessResponse {
  active: SeedPairView
  retired: SeedPairView[]
}

export interface HistoryOpen {
  id: string
  packId: string
  packName: string
  priceCents: number
  totalCents: number
  multiple: number
  best: Rarity
  nonce: number
  openedAt: string
  serverSeedHash: string
  clientSeed: string
  verifiable: boolean
  pulls: Array<{
    slotIndex: number
    slotLabel: string
    rarity: Rarity
    symbol: string
    kind: string
    name: string
    quantity: number
    priceAtOpen: number
    valueCents: number
    multiple: number
    rolls: number[]
  }>
}

export interface LedgerEntry {
  id: string
  kind: string
  amountCents: number
  balanceAfterCents: number
  memo: string | null
  ref: string | null
  createdAt: string
}

export interface SiteStats {
  opens: number
  users: number
  stakedCents: number
  returnedCents: number
  realisedRtp: number | null
}

export interface VerifyResponse {
  openId: string
  verified: boolean
  checks: { commitment: boolean; rolls: boolean; rarities: boolean; symbols: boolean }
  inputs: {
    serverSeed: string
    serverSeedHash: string
    clientSeed: string
    nonce: number
    packId: string
  }
  floats: number[]
  booked: Array<{ slotIndex: number; rarity: Rarity; symbol: string; quantity: number; valueCents: number }>
  replayed: Array<{ slotIndex: number; rarity: Rarity; symbol: string; quantity: number; valueCents: number }>
}
